import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from 'contexts/AuthContext';
import DashboardLayoutContent from 'components/ui/DashboardLayoutContent';
import DashboardAppShell from 'components/ui/DashboardAppShell';
import PanelHeader from 'components/ui/PanelHeader';
import Icon from 'components/AppIcon';
import { printService } from 'lib/printing/printService';
import {
  buildTestReceipt, buildImageCapabilityDiagnosticReceipt, buildCutCapabilityDiagnosticReceipt,
  buildLogoPositionDiagnosticReceipt, buildLogoGeometryDiagnosticReceipt, buildLogoPositionedDiagnosticReceipt,
  buildRightEdgeCalibrationDiagnosticReceipt, buildRightEdgeFineCalibrationDiagnosticReceipt,
  buildRightEdgeUltraFineCalibrationDiagnosticReceipt, buildLogoAspectRatioDiagnosticReceipt,
  buildLogoTestReceipt, buildCutTestReceipt,
} from 'lib/printing/receipts/renderEscPosReceipt';
import { buildFinalValidationReceipt } from 'lib/printing/receipts/buildSaleReceipt';
import { buildPrinterConfigKey, readPrinterConfig, writePrinterConfig } from 'lib/printing/printerConfigStorage';
import { listCompatibilityProfiles, getCompatibilityProfile, matchCompatibilityProfileId, getPresetProfileIdForPrinter, DEFAULT_COMPATIBILITY_PROFILE_ID } from 'lib/printing/receipts/printerCompatibilityProfiles';
import PrintConnectorSection from './components/PrintConnectorSection';
import { usePrintConnector } from './components/usePrintConnector';
import { buildPrintKey, CONNECTOR_TONE_CLASSES } from 'lib/printing/printRouting';

// PRINT-1 — pantalla aislada de configuración/prueba de impresión térmica.
// No toca CrmTerminal.jsx ni el flujo de cobro: solo permite elegir la
// impresora de este equipo (vía QZ Tray) y enviarle un ticket de prueba.
// La impresión automática al cobrar queda fuera de este alcance.
//
// PRINT-5 — esta pantalla pasa a tener DOS capas: (1) un flujo simple
// (impresora + modo Recomendado/Compatibilidad/Solo texto + pruebas
// básicas) pensado para un usuario normal, sin ningún término ESC/POS
// visible; (2) los diagnósticos técnicos que ya existían desde PRINT-4,
// sin cambios de comportamiento, ahora colapsados bajo "Opciones
// avanzadas". Elegir un modo solo materializa ids de capacidades ya
// validadas (ver printerCompatibilityProfiles.js) en la config local --
// nunca ejecuta lógica nueva de impresión.

const QZ_STATUS = {
  checking: { label: 'Verificando…', dot: 'bg-slate-300' },
  connected: { label: 'Conectado', dot: 'bg-emerald-500' },
  disconnected: { label: 'Desconectado', dot: 'bg-slate-300 border border-slate-400' },
};

const MODE_ICONS = { generic80: 'Sparkles', compatibility80: 'Wrench', textOnly80: 'FileText' };

export default function CrmPrintSettings() {
  const { business } = useAuth();
  const configKey = useMemo(() => buildPrinterConfigKey(business?.id), [business?.id]);

  const [config, setConfig] = useState(() => readPrinterConfig(null));
  const [qzStatus, setQzStatus] = useState('checking');
  const [printers, setPrinters] = useState([]);
  const [printersError, setPrintersError] = useState(null);
  const [loadingPrinters, setLoadingPrinters] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [printResult, setPrintResult] = useState(null); // { ok, message } | null

  // Walinka Print Connector: cuando está activo y emparejado es la ruta
  // principal de esta pantalla. QZ Tray no se consulta en ese caso.
  const connector = usePrintConnector(business?.id);
  const connectorActive = connector.active;
  const activePrinterName = connectorActive ? connector.printerName : config.printerName;
  const activePrinters = connectorActive ? connector.printers : printers;

  // Destino de impresión: mismo criterio que el TPV (printRouting.js).
  const printTargetFor = (kind) => (connectorActive
    ? { printerName: activePrinterName, connector: connector.provider, idempotencyKey: buildPrintKey(kind, business?.id) }
    : { printerName: config.printerName });

  useEffect(() => {
    setConfig(readPrinterConfig(configKey));
  }, [configKey]);

  const refreshPrinters = useCallback(async () => {
    setLoadingPrinters(true);
    setPrintersError(null);
    setPrintResult(null);
    try {
      await printService.connect();
      setQzStatus('connected');
      const list = await printService.listPrinters();
      setPrinters(list);
    } catch (err) {
      setQzStatus(printService.isAvailable() ? 'connected' : 'disconnected');
      setPrinters([]);
      setPrintersError(err?.message || 'No se pudo conectar con QZ Tray.');
    } finally {
      setLoadingPrinters(false);
    }
  }, []);

  // QZ Tray solo se consulta cuando el conector NO es la ruta activa.
  useEffect(() => { if (!connectorActive) refreshPrinters(); }, [refreshPrinters, connectorActive]);

  const handleRefreshPrinters = () => (connectorActive ? connector.refresh() : refreshPrinters());

  // PRINT-6 — materializa los campos de un perfil en una config (sin guardar).
  function applyProfileFields(base, profileId) {
    const profile = getCompatibilityProfile(profileId);
    return {
      ...base,
      profileId: profile.id,
      imageMode: profile.imageMode,
      cutStrategyId: profile.cutStrategyId,
      printLogo: profile.printLogo,
      autoCut: profile.autoCut,
      effectivePrintableWidthDots: profile.effectivePrintableWidthDots ?? null,
    };
  }

  // PRINT-6 — elegir una impresora con preset (ver PRINTER_PROFILE_PRESETS)
  // aplica su perfil. Elegir una impresora SIN preset nunca cambia el perfil
  // de otra: solo deshace el perfil exclusivo de preset (starTsp100) y vuelve
  // al Recomendado, para que la XP-80C u otra impresora no quede con ese perfil.
  // PRINT-6 — con el conector activo la impresora la guarda el conector
  // (connector.selectPrinter), NO el printerName de la configuración de QZ.
  // Aquí solo se aplica el perfil del preset a la config local de este
  // navegador, que es la que lee la venta. Otras impresoras no cambian nada.
  const handleSelectConnectorPrinter = (printerName) => {
    connector.selectPrinter(printerName);
    setPrintResult(null);
    const presetProfileId = getPresetProfileIdForPrinter(printerName || null);
    if (!presetProfileId) return;
    const next = applyProfileFields(config, presetProfileId);
    setConfig(next);
    if (configKey) writePrinterConfig(configKey, next);
  };

  const handleSelectPrinter = (printerName) => {
    const presetProfileId = getPresetProfileIdForPrinter(printerName || null);
    let next = { ...config, printerName: printerName || null };
    if (presetProfileId) {
      next = applyProfileFields(next, presetProfileId);
    } else if (config.profileId === 'starTsp100') {
      next = applyProfileFields(next, DEFAULT_COMPATIBILITY_PROFILE_ID);
    }
    setConfig(next);
    setPrintResult(null);
    if (configKey) writePrinterConfig(configKey, next);
  };

  // PRINT-5 — elegir un modo (Recomendado/Compatibilidad/Solo texto) solo
  // materializa los campos concretos de ese perfil (ver
  // printerCompatibilityProfiles.js) en la config guardada -- el resto de
  // la pantalla/el flujo de venta real (CrmTerminal.jsx) nunca vuelve a
  // mirar `profileId` para decidir qué imprimir, solo estos campos.
  const handleSelectMode = (profileId) => {
    const next = applyProfileFields(config, profileId);
    setConfig(next);
    setPrintResult(null);
    if (configKey) writePrinterConfig(configKey, next);
  };

  const handlePrintTest = async () => {
    if (!activePrinterName || printing) return;
    setPrinting(true);
    setPrintResult(null);
    try {
      const receipt = buildTestReceipt({
        businessName: business?.name,
        printerName: activePrinterName,
        paperWidthMm: config.paperWidthMm,
      });
      await printService.printReceipt(receipt, printTargetFor('test'));
      setPrintResult({ ok: true, message: 'Ticket de prueba enviado a la impresora.' });
    } catch (err) {
      setPrintResult({ ok: false, message: err?.message || 'No se pudo imprimir el ticket de prueba.' });
    } finally {
      setPrinting(false);
    }
  };

  // PRINT-5 — "Probar logo"/"Probar corte": mismo patrón simple que
  // handlePrintTest, sin ningún término ESC/POS en los mensajes -- pensado
  // para que un usuario normal confirme, con un solo click, si el logo o
  // el corte funcionan con el modo elegido, sin tener que interpretar un
  // diagnóstico técnico.
  const handlePrintLogoTest = async () => {
    if (!activePrinterName || printing) return;
    setPrinting(true);
    setPrintResult(null);
    try {
      const receipt = buildLogoTestReceipt({
        business,
        paperWidthMm: config.paperWidthMm,
        imageMode: config.imageMode,
        effectivePrintableWidthDots: config.effectivePrintableWidthDots,
      });
      await printService.printReceipt(receipt, printTargetFor('test'));
      setPrintResult({ ok: true, message: 'Prueba de logo enviada a la impresora.' });
    } catch (err) {
      setPrintResult({ ok: false, message: err?.message || 'No se pudo imprimir la prueba de logo.' });
    } finally {
      setPrinting(false);
    }
  };

  const handlePrintCutTest = async () => {
    if (!activePrinterName || printing) return;
    setPrinting(true);
    setPrintResult(null);
    try {
      const receipt = buildCutTestReceipt({ paperWidthMm: config.paperWidthMm, cutStrategyId: config.cutStrategyId });
      await printService.printReceipt(receipt, printTargetFor('test'));
      setPrintResult({ ok: true, message: 'Prueba de corte enviada a la impresora.' });
    } catch (err) {
      setPrintResult({ ok: false, message: err?.message || 'No se pudo imprimir la prueba de corte.' });
    } finally {
      setPrinting(false);
    }
  };

  // PRINT-4-BUG2 — diagnóstico de compatibilidad: envía por separado dos
  // variantes de comando ESC/POS estándar (ninguna específica de Star ni
  // de ningún otro fabricante -- ver escPosCapabilities.js) para que la
  // prueba física diga cuál interpreta realmente esta impresora. No
  // genera ninguna venta ni toca el flujo de cobro.
  const runDiagnosticPrint = async (buildReceipt, successMessage, failureMessage) => {
    if (!activePrinterName || printing) return;
    setPrinting(true);
    setPrintResult(null);
    try {
      const receipt = buildReceipt();
      await printService.printReceipt(receipt, printTargetFor('test'));
      setPrintResult({ ok: true, message: successMessage });
    } catch (err) {
      setPrintResult({ ok: false, message: err?.message || failureMessage });
    } finally {
      setPrinting(false);
    }
  };

  const handlePrintImageDiagnostic = () => runDiagnosticPrint(
    () => buildImageCapabilityDiagnosticReceipt({ paperWidthMm: config.paperWidthMm }),
    'Diagnóstico de imagen enviado a la impresora.',
    'No se pudo imprimir el diagnóstico de imagen.',
  );

  const handlePrintCutDiagnostic = () => runDiagnosticPrint(
    () => buildCutCapabilityDiagnosticReceipt({ paperWidthMm: config.paperWidthMm }),
    'Diagnóstico de corte enviado a la impresora.',
    'No se pudo imprimir el diagnóstico de corte.',
  );

  // PRINT-4-BUG6 — diagnóstico EXCLUSIVO de logo: marcas de margen
  // izquierdo/derecho esperado + el logo real del negocio, para confirmar
  // físicamente si queda dentro del área segura. No genera ninguna venta.
  const handlePrintLogoPositionDiagnostic = () => runDiagnosticPrint(
    () => buildLogoPositionDiagnosticReceipt({
      business, paperWidthMm: config.paperWidthMm, imageMode: config.imageMode,
    }),
    'Diagnóstico de logo enviado a la impresora.',
    'No se pudo imprimir el diagnóstico de logo.',
  );

  // PRINT-4-BUG8 — el corrimiento hacia la derecha persistió pese a BUG6/BUG7:
  // este diagnóstico imprime tres bloques de posición absoluta conocida
  // (izquierda/centro/derecha) por la MISMA ruta ESC * del logo, para
  // verificar físicamente -- sin ninguna suposición sobre el firmware --
  // dónde aparece realmente cada uno. No genera ninguna venta.
  const handlePrintLogoGeometryDiagnostic = () => runDiagnosticPrint(
    () => buildLogoGeometryDiagnosticReceipt({
      paperWidthMm: config.paperWidthMm, imageMode: config.imageMode,
    }),
    'Diagnóstico de geometría enviado a la impresora.',
    'No se pudo imprimir el diagnóstico de geometría.',
  );

  // PRINT-4-BUG9 — BUG8 confirmó físicamente que el margen horneado en
  // píxeles blancos NO posiciona nada en esta impresora (un bloque con
  // ~226 columnas de padding apareció comprimido contra el borde derecho,
  // uno con ~452 desapareció). Este diagnóstico prueba ESC $ (posición
  // absoluta) en su lugar, con bloques que nunca llevan padding -- solo
  // su ancho real (60 dots). No genera ninguna venta.
  const handlePrintLogoPositionedDiagnostic = () => runDiagnosticPrint(
    () => buildLogoPositionedDiagnosticReceipt({ paperWidthMm: config.paperWidthMm }),
    'Diagnóstico de posición explícita enviado a la impresora.',
    'No se pudo imprimir el diagnóstico de posición explícita.',
  );

  // PRINT-4-BUG10 — BUG9 confirmó que ESC $ posiciona bien, pero también
  // que el ancho nominal (512 a 80mm) no es el área realmente imprimible
  // (x=452 ya no cupo completo). Este diagnóstico ubica el límite exacto
  // con 5 bloques de 20 dots (sin relleno) en x=400/420/440/460/480. No
  // genera ninguna venta.
  const handlePrintRightEdgeCalibration = () => runDiagnosticPrint(
    () => buildRightEdgeCalibrationDiagnosticReceipt({
      paperWidthMm: config.paperWidthMm, imageMode: config.imageMode,
    }),
    'Diagnóstico de calibración de borde derecho enviado a la impresora.',
    'No se pudo imprimir el diagnóstico de calibración.',
  );

  // PRINT-4-BUG11 — la calibración gruesa ubicó el límite entre 420
  // (completo) y 440 (parcial/cortado), y reveló que coordenadas más
  // allá del límite hacen "wrap" (el bloque reaparece a la izquierda en
  // vez de cortarse). Esta calibración fina concentra bloques cada 4 dots
  // entre 420 y 440 para ubicar la última coordenada exacta. No genera
  // ninguna venta.
  const handlePrintRightEdgeFineCalibration = () => runDiagnosticPrint(
    () => buildRightEdgeFineCalibrationDiagnosticReceipt({
      paperWidthMm: config.paperWidthMm, imageMode: config.imageMode,
    }),
    'Diagnóstico de calibración fina de borde enviado a la impresora.',
    'No se pudo imprimir el diagnóstico de calibración fina.',
  );

  // PRINT-4-BUG12 — la calibración fina (x=420 a 440, bloques de 20 dots)
  // resultó completa en las 6 posiciones: el límite real está más allá de
  // 440. Esta ronda usa bloques de 8 dots en x=448/452/456/460/464/468,
  // deliberadamente por debajo de 480 (zona de wrap ya confirmada). No
  // genera ninguna venta.
  const handlePrintRightEdgeUltraFineCalibration = () => runDiagnosticPrint(
    () => buildRightEdgeUltraFineCalibrationDiagnosticReceipt({
      paperWidthMm: config.paperWidthMm, imageMode: config.imageMode,
    }),
    'Diagnóstico de calibración de borde (8 dots) enviado a la impresora.',
    'No se pudo imprimir el diagnóstico de calibración de 8 dots.',
  );

  // PRINT-4-BUG13 — con posición/ancho/layout ya validados físicamente,
  // queda el último problema: un logo circular sale como óvalo vertical.
  // Este diagnóstico imprime un cuadrado y un círculo de 100x100 dots por
  // la misma ruta de producción del logo, para medir el factor de
  // distorsión vertical real (sospecha: el avance ESC 3 8 entre franjas).
  // No aplica ninguna compensación, no genera ninguna venta.
  const handlePrintLogoAspectRatioDiagnostic = () => runDiagnosticPrint(
    () => buildLogoAspectRatioDiagnosticReceipt({
      paperWidthMm: config.paperWidthMm, imageMode: config.imageMode,
    }),
    'Diagnóstico de aspecto (cuadrado/círculo) enviado a la impresora.',
    'No se pudo imprimir el diagnóstico de aspecto.',
  );

  // PRINT-4-BUG3 — ejercita el camino de producción completo (logo real +
  // ítems + descuento + TOTAL destacado + corte) con una venta sintética,
  // para una última confirmación física antes de dejarlo activo para
  // ventas reales. No genera ninguna venta ni toca el flujo de cobro.
  const handlePrintFinalValidation = () => runDiagnosticPrint(
    () => buildFinalValidationReceipt({
      business,
      paperWidthMm: config.paperWidthMm,
      autoCut: config.autoCut,
      printLogo: config.printLogo,
      imageMode: config.imageMode,
      cutStrategyId: config.cutStrategyId,
      effectivePrintableWidthDots: config.effectivePrintableWidthDots,
    }),
    'Impresión final de validación enviada a la impresora.',
    'No se pudo imprimir la validación final.',
  );

  const status = connectorActive
    ? { dot: CONNECTOR_TONE_CLASSES[connector.status.tone], label: connector.status.label }
    : QZ_STATUS[qzStatus];
  const savedPrinterMissing = Boolean(activePrinterName)
    && (connectorActive ? connector.health === 'ok' : qzStatus === 'connected')
    && !activePrinters.includes(activePrinterName);
  const canPrint = connectorActive
    ? connector.status.canPrint && !printing
    : qzStatus === 'connected' && Boolean(config.printerName) && !printing;
  // PRINT-5 — una config guardada por PRINT-1 a PRINT-4 nunca tuvo
  // `profileId`; sanitizeConfig ya lo rellena con 'generic80', pero por si
  // alguna vez llega vacío/desconocido igual se infiere algo razonable
  // para resaltar en el selector -- ver matchCompatibilityProfileId.
  const selectedModeId = config.profileId || matchCompatibilityProfileId(config);

  return (
    <DashboardAppShell>
      <PanelHeader
        title={
          <div className="flex items-center gap-2">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-slate-900 text-white">
              <Icon name="Printer" size={16} />
            </span>
            <h1 className="text-base font-bold text-slate-900">Impresión del TPV</h1>
          </div>
        }
        subtitle={
          <p className="text-xs text-slate-500">Configura y prueba la impresora térmica de este equipo.</p>
        }
      />

      <DashboardLayoutContent innerClassName="lg:max-w-2xl">
        <div className="mb-6">
          <PrintConnectorSection connector={connector} />
        </div>

        <section aria-label="Impresión del TPV" className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">

          <div className="flex items-center justify-between gap-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Estado de impresión</p>
            <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-semibold text-slate-700">
              <span className={`h-2.5 w-2.5 rounded-full ${status.dot}`} />
              {status.label}
            </span>
          </div>

          {!connectorActive && qzStatus === 'disconnected' && (
            <div className="mt-3 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
              <Icon name="AlertTriangle" size={14} className="mt-0.5 shrink-0" />
              <span>
                No se detectó el servicio de impresión en este equipo. Verifica que esté instalado y
                en ejecución, luego pulsa "Actualizar impresoras".
                {printersError && <> ({printersError})</>}
              </span>
            </div>
          )}

          <div className="mt-4">
            <label htmlFor="print-printer-select" className="block text-xs font-semibold uppercase tracking-wide text-slate-500">
              Impresora
            </label>
            <select
              id="print-printer-select"
              value={activePrinterName || ''}
              onChange={(e) => (connectorActive ? handleSelectConnectorPrinter(e.target.value) : handleSelectPrinter(e.target.value))}
              disabled={connectorActive
                ? connector.health !== 'ok' || activePrinters.length === 0
                : qzStatus !== 'connected' || printers.length === 0}
              className="mt-1.5 w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-500 disabled:bg-slate-50 disabled:text-slate-400"
            >
              <option value="">
                {activePrinters.length === 0 ? 'Sin impresoras detectadas' : 'Selecciona una impresora'}
              </option>
              {activePrinters.map((name) => <option key={name} value={name}>{name}</option>)}
            </select>
            {savedPrinterMissing && (
              <p className="mt-1.5 text-xs text-amber-700">
                La impresora guardada ("{activePrinterName}") ya no aparece en este equipo.
              </p>
            )}
            {(connectorActive ? connector.health === 'ok' : qzStatus === 'connected') && activePrinters.length === 0 && !savedPrinterMissing && (
              <p className="mt-1.5 text-xs text-slate-400">
                El servicio de impresión está conectado, pero no se encontraron impresoras instaladas en este equipo.
              </p>
            )}
          </div>

          <div className="mt-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Papel</p>
            <p className="mt-1.5 text-sm font-semibold text-slate-700">{config.paperWidthMm} mm</p>
          </div>

          {/* PRINT-5 — selector de modo: sin ESC/POS a la vista. Cada
              opción es un perfil de printerCompatibilityProfiles.js;
              elegirla guarda de inmediato los campos concretos que ese
              perfil implica. */}
          <div className="mt-5">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Modo de impresión</p>
            <div className="mt-1.5 grid grid-cols-1 gap-2 sm:grid-cols-3">
              {listCompatibilityProfiles().map((profile) => {
                const selected = selectedModeId === profile.id;
                return (
                  <button
                    key={profile.id}
                    type="button"
                    onClick={() => handleSelectMode(profile.id)}
                    aria-pressed={selected}
                    className={`flex flex-col items-start gap-1 rounded-xl border p-3 text-left transition-colors ${
                      selected ? 'border-slate-900 bg-slate-900/5' : 'border-slate-200 hover:bg-slate-50'
                    }`}
                  >
                    <span className="flex items-center gap-1.5 text-sm font-semibold text-slate-800">
                      <Icon name={MODE_ICONS[profile.id] || 'Settings'} size={14} />
                      {profile.label}
                    </span>
                    <span className="text-xs text-slate-500">{profile.description}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="mt-5 flex flex-col gap-2 sm:flex-row">
            <button
              type="button"
              onClick={handleRefreshPrinters}
              disabled={loadingPrinters && !connectorActive}
              className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-xl border border-slate-300 px-3 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              <Icon name={loadingPrinters ? 'Loader2' : 'RefreshCw'} size={15} className={loadingPrinters ? 'animate-spin' : ''} />
              Actualizar impresoras
            </button>
            <button
              type="button"
              onClick={handlePrintTest}
              disabled={!canPrint}
              className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-xl bg-slate-900 px-3 py-2.5 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-40"
            >
              <Icon name={printing ? 'Loader2' : 'Printer'} size={15} className={printing ? 'animate-spin' : ''} />
              Imprimir ticket de prueba
            </button>
          </div>

          {/* PRINT-5 — pruebas simples, sin jerga técnica: validan logo y
              corte por separado con el modo actualmente elegido. */}
          <div className="mt-2 flex flex-col gap-2 sm:flex-row">
            <button
              type="button"
              onClick={handlePrintLogoTest}
              disabled={!canPrint || config.printLogo === false}
              className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-xl border border-slate-300 px-3 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-40"
            >
              <Icon name={printing ? 'Loader2' : 'Image'} size={15} className={printing ? 'animate-spin' : ''} />
              Probar logo
            </button>
            <button
              type="button"
              onClick={handlePrintCutTest}
              disabled={!canPrint}
              className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-xl border border-slate-300 px-3 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-40"
            >
              <Icon name={printing ? 'Loader2' : 'Scissors'} size={15} className={printing ? 'animate-spin' : ''} />
              Probar corte
            </button>
          </div>
          {config.printLogo === false && (
            <p className="mt-1.5 text-xs text-slate-400">
              El modo "Solo texto" no imprime logo -- cambia a "Recomendado" o "Compatibilidad" para probarlo.
            </p>
          )}

          <div className="mt-5 flex items-start gap-2 rounded-xl border border-slate-200 bg-slate-50 p-3">
            <Icon name="LifeBuoy" size={15} className="mt-0.5 shrink-0 text-slate-400" />
            <div>
              <p className="text-xs font-semibold text-slate-700">¿La impresión no sale correctamente?</p>
              <p className="mt-0.5 text-xs leading-relaxed text-slate-500">Prueba otro modo de impresión y vuelve a imprimir el ticket de prueba. Si el problema continúa, contacta a soporte.</p>
            </div>
          </div>

          {printResult && (
            <div className={`mt-4 flex items-start gap-2 rounded-xl border p-3 text-xs ${printResult.ok ? 'border-emerald-200 bg-emerald-50 text-emerald-800' : 'border-rose-200 bg-rose-50 text-rose-700'}`}>
              <Icon name={printResult.ok ? 'CheckCircle2' : 'AlertCircle'} size={14} className="mt-0.5 shrink-0" />
              <span>{printResult.message}</span>
            </div>
          )}

          <p className="mt-5 text-xs text-slate-400">
            Los cambios se guardan para este equipo. Imprime un ticket de prueba para confirmar que todo quedó listo.
          </p>
        </section>

      </DashboardLayoutContent>
    </DashboardAppShell>
  );
}
